import { isPlatformServer } from '@angular/common';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { StrapiService } from 'app/modules/landing/dealers/strapi.service';
import { slugify } from 'app/core/utils/slugify';
import { environment } from 'environments/environment';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { PublicContentSnapshotService } from './public-content-snapshot.service';

type ServerProcessLike = {
    env?: Record<string, string | undefined>;
};

let _ppdGateLogged = false;

@Injectable({
    providedIn: 'root',
})
export class PublicPageDataService {
    private readonly _platformId = inject(PLATFORM_ID);

    constructor(
        private strapiService: StrapiService,
        private snapshotService: PublicContentSnapshotService
    ) {}

    private shouldUseSnapshot(): boolean {
        const isServer = isPlatformServer(this._platformId);
        let result: boolean;
        let envVal: string | undefined;
        if (!isServer) {
            result = !(environment as { enableLivePublicContent?: boolean }).enableLivePublicContent;
        } else {
            envVal = this.getServerEnv('ENABLE_LIVE_PUBLIC_CONTENT');
            result = envVal !== 'true';
        }
        if (isServer && !_ppdGateLogged) {
            _ppdGateLogged = true;
            const hasProcess = typeof (globalThis as typeof globalThis & { process?: ServerProcessLike }).process !== 'undefined';
            console.log(`[PPD] isServer=true hasProcess=${hasProcess} ENABLE_LIVE_PUBLIC_CONTENT=${envVal} useSnapshot=${result}`);
        }
        return result;
    }

    private getSnapshotPage<T>(key: string): Observable<T | null> {
        if (!this.shouldUseSnapshot() || !this.snapshotService.hasPage(key)) {
            return of(null);
        }
        return from(this.snapshotService.getPage<T>(key));
    }

    private getSnapshotEntity<T>(type: string, key: string): Observable<T | null> {
        if (!this.shouldUseSnapshot() || !this.snapshotService.hasEntity(type, key)) {
            return of(null);
        }
        return from(this.snapshotService.getEntity<T>(type, key));
    }

    private snapshotOrLive<T>(
        snapshot$: Observable<T | null>,
        live: () => Observable<T>
    ): Observable<T> {
        return snapshot$.pipe(switchMap(s => (s !== null && s !== undefined ? of(s) : live())));
    }

    private getServerEnv(key: string): string | undefined {
        if (!isPlatformServer(this._platformId)) {
            return undefined;
        }

        const serverProcess = (globalThis as typeof globalThis & {
            process?: ServerProcessLike;
        }).process;

        return serverProcess?.env?.[key];
    }

    getLandingPageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('landing'),
            () =>
                forkJoin({
                    homeVideo: this.strapiService.getHomeVideo(),
                    homeVideoMain: this.strapiService.getHomeVideoMain(),
                    homeVideoLicenseSection: this.strapiService.getHomeVideoLicenseSection(),
                    heritage: this.strapiService.getHeritagePage(),
                    vehicles: this.strapiService.getVehicleModels(),
                    footerVehicles: this.strapiService.getVehicleModels(),
                }).pipe(
                    map((result: any) => ({
                        homeVideo: result.homeVideo?.data ?? null,
                        homeVideoMain: result.homeVideoMain?.data ?? null,
                        homeVideoLicenseSection: result.homeVideoLicenseSection?.data ?? null,
                        heritage: result.heritage?.data ?? null,
                        vehicles: this.mapVehicleModels(result.vehicles?.data ?? []),
                        footerVehicles: (result.footerVehicles?.data ?? []).map((vehicle: any) => ({
                            id: vehicle.id,
                            documentId: vehicle.documentId,
                            name: vehicle.name,
                            hasSubVehicles: vehicle.hasSubVehicles || false,
                        })),
                    }))
                )
        );
    }

    getVehiclesPageData(): Observable<any[]> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any[]>('vehicles'),
            () =>
                this.strapiService
                    .getVehicleModels()
                    .pipe(map((response: any) => this.mapVehicleModels(response?.data ?? [])))
        );
    }

    getSubVehiclesPageDataBySlug(slug: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('subVehicles', slug),
            () => this.getSubVehiclesPageDataLive(slug)
        );
    }

    private getSubVehiclesPageDataLive(slug: string): Observable<any> {
        return this.strapiService.getAllVehicleModels().pipe(
            map((response: any) => response?.data ?? []),
            map((models: any[]) => {
                const matches = models.filter(
                    (model: any) => slugify(model.name || model.title || '') === slug
                );
                // Prefer a top-level match, but fall back to a sub-vehicle so that
                // sub-vehicles which are themselves parents (2nd+ level) resolve too.
                return matches.find((model: any) => !model.isSubVehicle) || matches[0];
            }),
            switchMap((parentModel: any) => {
                if (!parentModel?.id) {
                    return of(null);
                }

                return this.strapiService.getVehicleModelById(parentModel.id).pipe(
                    map((response: any) => response?.data?.[0] ?? null),
                    map((resolvedModel: any) => {
                        if (!resolvedModel) {
                            return null;
                        }

                        const subVehicles = this.mapVehicleModels(resolvedModel.vehicles ?? []);

                        return {
                            parentModel: {
                                ...resolvedModel,
                                imageUrl: this.strapiService.getFullImageUrl(
                                    resolvedModel.image?.url
                                ),
                            },
                            subVehicles,
                        };
                    })
                );
            })
        );
    }

    getVehicleDetailPageDataBySlug(slug: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('vehicleDetails', slug).pipe(
                tap((s: any) => {
                    if (s !== null && s !== undefined) {
                        console.log(`[PPD] vehicleDetail slug=${slug} source=snapshot`);
                    }
                })
            ),
            () => {
                console.log(`[PPD] vehicleDetail slug=${slug} source=strapi`);
                return this.getVehicleDetailPageDataLive(slug);
            }
        );
    }

    private getVehicleDetailPageDataLive(slug: string): Observable<any> {
        return this.strapiService.getAllVehicleModels().pipe(
            tap({
                next: (r: any) => console.log(`[PPD] getAllVehicleModels OK items=${r?.data?.length ?? 0}`),
                error: (e: any) => console.error(`[PPD] getAllVehicleModels ERR ${e?.message ?? e}`),
            }),
            map((response: any) => response?.data ?? []),
            map((models: any[]) => {
                const matches = models.filter(
                    (m: any) => slugify(m.name || m.title || '') === slug
                );
                const subMatch = matches.find((m: any) => m.isSubVehicle === true);
                const picked = subMatch || matches[0];
                console.log(`[PPD] slug=${slug} matches=${matches.length} picked=id=${picked?.id ?? 'none'} documentId=${picked?.documentId ?? 'none'} isSub=${picked?.isSubVehicle}`);
                return picked;
            }),
            switchMap((matchedModel: any) => {
                if (!matchedModel?.documentId) {
                    return of(null);
                }

                return this.strapiService.getVehicleDetailByModelDocumentId(matchedModel.documentId).pipe(
                    tap({
                        next: (r: any) => {
                            const m = r?.data?.[0];
                            console.log(`[PPD] getVehicleDetailByModelDocumentId documentId=${matchedModel.documentId} OK hits=${r?.data?.length ?? 0} vehicle_info=${m?.vehicle_info ? 'id=' + m.vehicle_info.id : 'NULL'}`);
                        },
                        error: (e: any) => console.error(`[PPD] getVehicleDetailByModelDocumentId ERR ${e?.message ?? e}`),
                    }),
                    map((response: any) => response?.data?.[0] ?? null),
                    switchMap((vehicleModel: any) => {
                        const vehicleInfoId = vehicleModel?.vehicle_info?.id;

                        if (!vehicleModel || !vehicleInfoId) {
                            return of(null);
                        }

                        return forkJoin({
                            core: this.strapiService.getVehicleDetailCore(vehicleInfoId),
                            specifications:
                                this.strapiService.getVehicleDetailSpecifications(vehicleInfoId),
                            paintOptions:
                                this.strapiService.getVehicleDetailPaintOptions(vehicleInfoId),
                            view360: this.strapiService.get360Images(vehicleInfoId),
                            baseColors: this.strapiService.getBaseColors(),
                        }).pipe(
                            map((result: any) => {
                                const vehicleDetail = result.core?.data?.[0] ?? null;
                                const specifications =
                                    result.specifications?.data?.[0]?.specification ?? [];
                                const paintOptions =
                                    result.paintOptions?.data?.[0]?.paint_options ?? [];
                                const threeSixtyViewer =
                                    result.view360?.data?.[0]?.threeSixtyViewer ?? [];

                                return {
                                    vehicleModel,
                                    vehicleDetail: vehicleDetail
                                        ? {
                                              ...vehicleDetail,
                                              specification: specifications,
                                              paint_options: paintOptions,
                                              threeSixtyViewer,
                                          }
                                        : null,
                                    baseColors: result.baseColors?.data ?? [],
                                };
                            })
                        );
                    })
                );
            })
        );
    }

    getAboutPageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('about'),
            () =>
                forkJoin({
                    aboutData: this.strapiService.getAboutUs(),
                    aboutUsMiddleContent: this.strapiService.getAboutUsMiddleContent(),
                }).pipe(
                    map((result: any) => ({
                        aboutData: result.aboutData?.data ?? null,
                        aboutUsMiddleContent: result.aboutUsMiddleContent?.data ?? null,
                    }))
                )
        );
    }

    getDealersPageData(): Observable<any> {
        return this.strapiService.getAllDealers().pipe(
            map((response: any) => response?.data ?? {}),
            map((responseData: any) => ({
                ...responseData,
                dealers: (responseData.dealers ?? []).map((dealer: any) => ({
                    ...dealer,
                    logoUrl: this.strapiService.getFullImageUrl(dealer.logo?.url),
                    countryFlagUrl: this.strapiService.getFullImageUrl(dealer.countryFlag?.url),
                })),
            }))
        );
    }

    getFactoryPageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('factory'),
            () =>
                this.strapiService
                    .getFactory()
                    .pipe(map((response: any) => response?.data ?? null))
        );
    }

    getFaqPageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('faq'),
            () =>
                forkJoin({
                    faqItems: this.strapiService.getFAQItems(),
                    faqCategories: this.strapiService.getFAQCategories(),
                }).pipe(
                    map((result: any) => ({
                        faqItems: (result.faqItems?.data ?? []).map((item: any) => ({
                            ...item,
                            isExpanded: false,
                        })),
                        faqCategories: result.faqCategories?.data ?? [],
                    }))
                )
        );
    }

    getGalleryPageData(): Observable<any[]> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any[]>('gallery'),
            () =>
                this.strapiService.getParentGalleryCategories().pipe(
                    map((response: any) => response?.data ?? []),
                    map((categories: any[]) =>
                        categories.map((category: any) => ({
                            id: category.id,
                            documentId: category.documentId,
                            title: category.title || category.name,
                            thumbnailImage: category.thumbnailImage
                                ? this.strapiService.getFullImageUrl(
                                      category.thumbnailImage.formats?.medium?.url ||
                                          category.thumbnailImage.formats?.small?.url ||
                                          category.thumbnailImage.url
                                  ) || ''
                                : '',
                            thumbnailImageData: category.thumbnailImage,
                            imageCount: category.images?.length || 0,
                            order: category.order || category.sequence || 0,
                            isVideo: category.isVideo || false,
                        }))
                    )
                )
        );
    }

    getGalleryCategoryPageDataBySlug(slug: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('galleryCategories', slug),
            () => this.getGalleryCategoryPageDataLive(slug)
        );
    }

    private getGalleryCategoryPageDataLive(slug: string): Observable<any> {
        return this.strapiService.getGalleryCategories().pipe(
            map((response: any) => response?.data ?? []),
            map((categories: any[]) =>
                categories.find((category: any) => slugify(category.title || category.name) === slug)
            ),
            switchMap((matchedCategory: any) => {
                if (!matchedCategory?.id) {
                    return of(null);
                }

                return this.strapiService.getGalleryCategoryByDocumentId(matchedCategory.id).pipe(
                    map((response: any) => response?.data?.[0] ?? null),
                    switchMap((categoryData: any) => {
                        if (!categoryData) {
                            return of(null);
                        }

                        const subCategoryIds = (categoryData.sub_categories ?? []).map(
                            (subCategory: any) => subCategory.id
                        );

                        if (!subCategoryIds.length) {
                            return of(this.mapGalleryCategoryPageData(categoryData, []));
                        }

                        return this.strapiService.getGalleryCategoriesByIds(subCategoryIds).pipe(
                            map((response: any) => response?.data ?? []),
                            map((subCategories: any[]) =>
                                this.mapGalleryCategoryPageData(categoryData, subCategories)
                            )
                        );
                    })
                );
            })
        );
    }

    getLicensePageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('license'),
            () =>
                forkJoin({
                    licenseData: this.strapiService.getLicense(),
                    licenseGalleryTop: this.strapiService.getLicenseGalleryTop(),
                    licenseGalleryBottom: this.strapiService.getLicenseGalleryBottom(),
                    homeVideoLicenseSection: this.strapiService.getHomeVideoLicenseSection(),
                }).pipe(
                    map((result: any) => ({
                        licenseData: result.licenseData?.data ?? null,
                        licenseGalleryTop: this.extractLicenseGalleryItems(result.licenseGalleryTop?.data ?? []),
                        licenseGalleryBottom: this.extractLicenseGalleryItems(
                            result.licenseGalleryBottom?.data ?? []
                        ),
                        homeVideoLicenseSection: result.homeVideoLicenseSection?.data ?? null,
                    }))
                )
        );
    }

    getPrivacyPolicyPageData(): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotPage<any>('privacy-policy'),
            () =>
                this.strapiService
                    .getPrivacyPolicy()
                    .pipe(map((response: any) => response?.data ?? null))
        );
    }

    getInventoryPageData(): Observable<any> {
        return this.getSnapshotPage<any>('inventory').pipe(
            switchMap(snapshot =>
                snapshot?.browse ? of(snapshot) : this.getInventoryPageDataLive()
            )
        );
    }

    private getInventoryPageDataLive(): Observable<any> {
        return forkJoin({
            vehicleModels: this.strapiService.getInventoryBrowseVehicles().pipe(catchError(() => of({ data: [] }))),
            inventoryItems: this.strapiService.getInventoryBrowseItems(1, 12).pipe(catchError(() => of(null))),
            baseColors: this.strapiService.getBaseColors().pipe(catchError(() => of({ data: [] }))),
        }).pipe(
            map((result: any) => ({
                browse: true,
                vehicleModels: result.vehicleModels?.data ?? [],
                inventoryItems: result.inventoryItems?.data ?? [],
                pagination: result.inventoryItems?.meta?.pagination ?? {
                    page: 1,
                    pageCount: 0,
                    total: 0,
                    pageSize: 12,
                },
                baseColors: result.baseColors?.data ?? [],
            }))
        );
    }

    getInventorySubVehiclesPageDataBySlug(slug: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('inventorySubVehicles', slug),
            () => this.getInventorySubVehiclesPageDataLive(slug)
        );
    }

    private getInventorySubVehiclesPageDataLive(slug: string): Observable<any> {
        return this.strapiService.getAllVehicleModels().pipe(
            map((response: any) => response?.data ?? []),
            map((models: any[]) =>
                models.find(
                    (m: any) =>
                        !m.isSubVehicle && slugify(m.name || m.title || '') === slug
                )
            ),
            switchMap((parentModel: any) => {
                if (!parentModel?.id) {
                    return of(null);
                }

                return this.strapiService.getVehicleModelById(parentModel.id).pipe(
                    map((response: any) => response?.data?.[0] ?? null),
                    switchMap((resolved: any) => {
                        if (!resolved) {
                            return of(null);
                        }

                        const subVehicles = resolved.vehicles ?? [];
                        if (!subVehicles.length) {
                            return of({
                                parentVehicle: resolved,
                                subVehicles: [],
                            });
                        }

                        const enriched$ = subVehicles.map((sv: any) =>
                            forkJoin({
                                inventory: this.strapiService
                                    .getInventoryItemsPaginated(1, 1, sv.id)
                                    .pipe(catchError(() => of(null))),
                                randomImage: this.strapiService
                                    .getRandomImageBySubvehicle(sv.documentId)
                                    .pipe(catchError(() => of(null))),
                            }).pipe(
                                map((result: any) => {
                                    const total =
                                        result.inventory?.meta?.pagination?.total ?? 0;
                                    const imageData =
                                        result.randomImage?.inventoryItem?.image ||
                                        result.randomImage?.image ||
                                        null;
                                    return {
                                        ...sv,
                                        inventoryCount: total,
                                        inventoryImage: imageData,
                                    };
                                })
                            )
                        );

                        return forkJoin(enriched$).pipe(
                            map((enrichedSubs: any[]) => ({
                                parentVehicle: resolved,
                                subVehicles: enrichedSubs,
                            }))
                        );
                    })
                );
            })
        );
    }

    getInventoryInfoPageDataBySlug(slug: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('inventoryInfo', slug),
            () => this.getInventoryInfoPageDataLive(slug)
        );
    }

    private getInventoryInfoPageDataLive(slug: string): Observable<any> {
        return this.strapiService.getInventoryBrowseVehicles().pipe(
            map((response: any) => response?.data ?? []),
            switchMap((parentModels: any[]) => {
                const allModels = [
                    ...parentModels,
                    ...parentModels.flatMap((model: any) => model.vehicles || []),
                ];
                const match = allModels.find((m: any) => slugify(m.name || m.title || '') === slug);
                if (!match) {
                    return of(null);
                }

                let parentVehicleName = '';
                let vehicleIds: any[] = [match.id];
                if (match.isSubVehicle) {
                    const parent = parentModels.find((v: any) =>
                        (v.vehicles || []).some((child: any) => child.id === match.id)
                    );
                    parentVehicleName = parent?.name ?? '';
                } else {
                    vehicleIds = [match.id, ...(match.vehicles || []).map((vehicle: any) => vehicle.id)];
                }

                return forkJoin({
                    inventoryItems: this.strapiService.getInventoryBrowseItems(1, 12, { vehicleIds }),
                    baseColors: this.strapiService.getBaseColors(),
                }).pipe(
                    map((result: any) => ({
                        browse: true,
                        vehicleModels: parentModels,
                        vehicleInfo: match,
                        parentVehicleName,
                        inventoryItems: result.inventoryItems?.data ?? [],
                        pagination: result.inventoryItems?.meta?.pagination ?? {
                            page: 1,
                            pageCount: 0,
                            total: 0,
                            pageSize: 12,
                        },
                        baseColors: result.baseColors?.data ?? [],
                    }))
                );
            })
        );
    }

    getInventoryDetailPageDataByChassis(chassis: string): Observable<any> {
        return this.snapshotOrLive(
            this.getSnapshotEntity<any>('inventoryDetails', chassis),
            () =>
                this.strapiService.getInventoryItemByChassis(chassis).pipe(
                    map((response: any) => response?.data?.[0] ?? null),
                    map((item: any) => {
                        if (!item) {
                            return { inventoryItem: null };
                        }

                        return {
                            inventoryItem: {
                                ...item,
                                imageUrl: this.strapiService.getOptimizedImageUrl(item.image, 'large'),
                                dealerLogoUrl: this.strapiService.getOptimizedImageUrl(
                                    item.dealer?.logo, 'thumbnail'
                                ),
                                galleryUrls:
                                    item.gallery?.map((img: any) => ({
                                        ...img,
                                        thumbnailUrl: this.strapiService.getOptimizedImageUrl(img, 'medium'),
                                        fullUrl: this.strapiService.getOptimizedImageUrl(img, 'large'),
                                        lqipUrl: this.strapiService.getOptimizedImageUrl(img, 'thumbnail'),
                                        srcset: this.strapiService.getImageSrcset(img),
                                    })) ?? [],
                            },
                        };
                    })
                )
        );
    }

    getNewsPageData(page: number = 1, pageSize: number = 6): Observable<any> {
        const snapshot$ = page === 1 ? this.getSnapshotPage<any>('news') : of(null);
        return this.snapshotOrLive(
            snapshot$,
            () =>
                this.strapiService.getNewsItems(page, pageSize).pipe(
                    map((response: any) => ({
                        items: response?.data ?? [],
                        pagination: response?.meta?.pagination ?? {
                            page,
                            pageCount: 0,
                            total: 0,
                            pageSize,
                        },
                    }))
                )
        );
    }

    getEventsPageData(page: number = 1, pageSize: number = 3): Observable<any> {
        const snapshot$ = page === 1 ? this.getSnapshotPage<any>('events') : of(null);
        return this.snapshotOrLive(
            snapshot$,
            () =>
                this.strapiService.getEvents(page, pageSize).pipe(
                    map((response: any) => ({
                        items: response?.data ?? [],
                        pagination: response?.meta?.pagination ?? {
                            page,
                            pageCount: 0,
                            total: 0,
                            pageSize,
                        },
                    }))
                )
        );
    }

    getTestimonialsPageData(page: number = 1, pageSize: number = 6): Observable<any> {
        const snapshot$ = page === 1 ? this.getSnapshotPage<any>('testimonials') : of(null);
        return this.snapshotOrLive(
            snapshot$,
            () =>
                this.strapiService.getTestimonialsPaginated(page, pageSize).pipe(
                    map((response: any) => ({
                        items: response?.data ?? [],
                        pagination: response?.meta?.pagination ?? {
                            page,
                            pageCount: 0,
                            total: 0,
                            pageSize,
                        },
                    }))
                )
        );
    }

    private extractLicenseGalleryItems(entries: any[]): any[] {
        return entries.flatMap((entry: any) => {
            const normalizedEntry = entry?.attributes ? { id: entry.id, ...entry.attributes } : entry;
            return (normalizedEntry?.galleryItems ?? []).map((item: any) =>
                item?.attributes ? { id: item.id, ...item.attributes } : item
            );
        });
    }

    private mapGalleryCategoryPageData(categoryData: any, subCategories: any[]): any {
        const mappedSubCategories = subCategories.map((subCategory: any) => ({
            id: subCategory.id,
            documentId: subCategory.documentId,
            title: subCategory.title,
            description: `Gallery category for ${subCategory.title}`,
            thumbnailImage: this.getGalleryImageUrl(subCategory.thumbnailImage, 'medium'),
            imageCount: subCategory.images?.length || 0,
            subCategories: [],
            images: [],
        }));

        const images = (categoryData.images ?? []).map((image: any, index: number) => {
            const isVideo = image.mime?.startsWith('video/') || !!image.videoUrl;

            return {
                id: `${categoryData.id}_${index}`,
                title: image.name || `Image ${index + 1}`,
                description: `Gallery image from ${categoryData.title}`,
                imageUrl: isVideo
                    ? this.strapiService.getFullImageUrl(image.url)
                    : this.getGalleryImageUrl(image, 'large'),
                thumbnailUrl: isVideo
                    ? this.getGalleryVideoThumbnail(image)
                    : this.getGalleryImageUrl(image, 'medium'),
                isVideo,
                videoUrl: isVideo
                    ? this.strapiService.getFullImageUrl(image.videoUrl || image.url)
                    : undefined,
                order: index,
                width: image.width,
                height: image.height,
                formats: image.formats,
            };
        });

        return {
            id: categoryData.id,
            documentId: categoryData.documentId,
            title: categoryData.title,
            description: `Gallery category for ${categoryData.title}`,
            thumbnailImage: this.getGalleryImageUrl(categoryData.thumbnailImage, 'medium'),
            imageCount: images.length,
            subCategories: mappedSubCategories,
            images,
            processedVideoUrls: this.normalizeVideoUrls(categoryData.videoUrls),
            originalVideoUrls: Array.isArray(categoryData.videoUrls)
                ? categoryData.videoUrls
                : categoryData.videoUrls
                  ? [categoryData.videoUrls]
                  : [],
        };
    }

    private mapVehicleModels(models: any[]): any[] {
        return models.map((model: any) => ({
            ...model,
            imageUrl: this.strapiService.getFullImageUrl(model.image?.url) || model.imageUrl || null,
            subVehicles:
                model.subVehicles?.map((subVehicle: any) => ({
                    ...subVehicle,
                    imageUrl:
                        this.strapiService.getFullImageUrl(subVehicle.image?.url) ||
                        subVehicle.imageUrl ||
                        null,
                })) || [],
        }));
    }

    private getGalleryImageUrl(
        imageData: any,
        preferredSize: 'small' | 'medium' | 'large' = 'medium'
    ): string {
        if (!imageData) {
            return '';
        }

        const selectedFormat =
            preferredSize === 'small'
                ? imageData.formats?.small || imageData.formats?.thumbnail || imageData.formats?.medium
                : preferredSize === 'large'
                  ? imageData.formats?.large || imageData.formats?.medium || imageData.formats?.xlarge
                  : imageData.formats?.medium || imageData.formats?.small || imageData.formats?.large;

        const relativeUrl = selectedFormat?.url || imageData.url || '';
        return relativeUrl ? this.strapiService.getFullImageUrl(relativeUrl) || '' : '';
    }

    private getGalleryVideoThumbnail(image: any): string {
        const videoUrl = image.videoUrl || '';
        const youtubeMatch = videoUrl.match(
            /(?:youtube\.com\/(?:[^/]+\/.+|(?:v|embed|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
        );

        if (youtubeMatch?.[1]) {
            return `https://img.youtube.com/vi/${youtubeMatch[1]}/hqdefault.jpg`;
        }

        return (
            this.strapiService.getFullImageUrl(
                image.previewUrl || image.formats?.thumbnail?.url || image.thumbnailUrl
            ) || ''
        );
    }

    private normalizeVideoUrls(videoUrls: any): string[] {
        const items = Array.isArray(videoUrls) ? videoUrls : videoUrls ? [videoUrls] : [];
        return items
            .map((item: any) => item?.value || item)
            .filter(Boolean)
            .map((url: string) => this.toYouTubeEmbedUrl(url));
    }

    private toYouTubeEmbedUrl(url: string): string {
        const regexes = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
            /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
        ];

        for (const regex of regexes) {
            const match = url.match(regex);
            if (match?.[1]) {
                return `https://www.youtube.com/embed/${match[1]}`;
            }
        }

        return url;
    }
}
